import React, { useEffect, useMemo, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlassIcon as MagnifyingGlass, SparkleIcon as Sparkle, CompassIcon as Compass, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/** Browse-all-breeds discovery page.
 *
 *  Shows the entire seeded catalog (purebreds + exotics) grouped by
 *  breed group so a buyer who isn't sure what they want can scroll
 *  through every option, click into a breed, and immediately see the
 *  marketplace listings filtered to that breed. The "Exotic only"
 *  toggle re-renders the page focused on the rarer breeds we register.
 */
export default function BreedsCatalog() {
  const [rows, setRows] = useState([]);
  const [exoticOnly, setExoticOnly] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/breeds/catalog").then((r) => setRows(r.data?.breeds || []));
  }, []);

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (exoticOnly && !r.exotic) return false;
      if (lower && !r.name.toLowerCase().includes(lower) && !r.origin.toLowerCase().includes(lower)) {
        return false;
      }
      return true;
    });
  }, [rows, exoticOnly, q]);

  // Bucket by group for a tidy directory feel.
  const byGroup = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const k = r.group || "Other";
      (map[k] = map[k] || []).push(r);
    });
    const order = ["Sporting", "Hound", "Working", "Terrier", "Toy", "Non-Sporting", "Herding", "Exotic", "Other"];
    return order.filter((g) => map[g]).map((g) => [g, map[g]]);
  }, [filtered]);

  return (
    <AppShell>
      <SeoHead
        title="All Breeds · Purebred & Exotic Dog Catalog · Armstrong K9 Registry"
        description="Browse the complete catalog of purebred and exotic dog breeds we register. Filter by group, find rare exotic breeds, and click through to current listings."
        url={`${webOrigin()}/breeds`}
        type="website"
        breadcrumbs={[
          { name: "Home", url: `${webOrigin()}/` },
          { name: "Breeds", url: `${webOrigin()}/breeds` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "All Dog Breeds · Armstrong K9 Registry",
          "description": "Purebred and exotic breeds we register.",
          "url": `${webOrigin()}/breeds`,
        }}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            Catalog
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Browse all breeds
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Every purebred and exotic breed we register, organized by
            breed group. Click any breed to see the dogs currently listed
            for sale — even rare or unfamiliar breeds you might never
            have considered.
          </p>
        </div>
        <Link to="/marketplace">
          <Button variant="outline" className="rounded-full border-[#E5E5E5]">
            <Compass size={14} className="mr-1.5" weight="duotone" /> Back to marketplace
          </Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-6 max-w-2xl">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or country of origin"
            className="pl-9"
            data-testid="breeds-search"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={exoticOnly}
            onChange={(e) => setExoticOnly(e.target.checked)}
            className="accent-[#C9A227]"
            data-testid="breeds-exotic-toggle"
          />
          <Sparkle size={14} weight="duotone" className="text-gold" /> Exotic only
        </label>
      </div>

      <p className="text-xs text-[#6B6B6B] mb-6" data-testid="breeds-count">
        Showing <strong>{filtered.length}</strong> of {rows.length} breeds.
      </p>

      <div className="space-y-12">
        {byGroup.map(([group, items]) => (
          <section key={group} data-testid={`breeds-group-${group.toLowerCase()}`}>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-spectral text-2xl tracking-tight">{group}</h2>
              <span className="text-xs text-[#6B6B6B]">
                {items.length} breed{items.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((b) => (
                <Link
                  key={b.id}
                  to={`/marketplace?breeds=${encodeURIComponent(b.name)}`}
                  className="group bg-white border border-[#E5E5E5] hover:border-[#C9A227]/60 hover:shadow-luxury-light rounded-xl p-4 flex flex-col gap-1.5 transition-all"
                  data-testid={`breed-card-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-spectral text-lg tracking-tight">{b.name}</h3>
                    {b.exotic && (
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#C9A227]/15 text-[#8a6f10] border border-[#C9A227]/40 rounded-full px-2 py-0.5 flex-shrink-0">
                        Exotic
                      </span>
                    )}
                  </div>
                  {b.origin && (
                    <div className="text-[11px] text-[#6B6B6B]">{b.origin}</div>
                  )}
                  {b.description && (
                    <p className="text-xs text-[#3F3F3F] mt-1 leading-relaxed line-clamp-3">
                      {b.description}
                    </p>
                  )}
                  <div className="text-xs text-ink mt-2 inline-flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    See listings <ArrowRight size={12} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </AppShell>
  );
}
